package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.FadeInKeyframes
import com.sddamico.components.motion.rememberReducedMotion
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.EventListener

val ModalCardInKeyframes = Keyframes {
    from { Modifier.opacity(0).styleModifier { property("transform", "translateY(8px) scale(0.96)") } }
    to { Modifier.opacity(1).styleModifier { property("transform", "none") } }
}

val ModalCardOutKeyframes = Keyframes {
    from { Modifier.opacity(1).styleModifier { property("transform", "none") } }
    to { Modifier.opacity(0).styleModifier { property("transform", "scale(0.98)") } }
}

val FadeOutKeyframes = Keyframes {
    from { Modifier.opacity(1) }
    to { Modifier.opacity(0) }
}

class ModalScope internal constructor(private val requestDismiss: (() -> Unit) -> Unit) {
    /** Plays the exit animation, then runs [then] — usually the caller's onClose or onSuccess. */
    fun dismiss(then: () -> Unit) = requestDismiss(then)
}

/**
 * A centered dialog over a dimmed backdrop. It scales up out of the page on open and fades back on close; closing
 * by backdrop click or Escape runs [onDismiss] after the exit animation. Content closes the dialog with
 * [ModalScope.dismiss] so it gets the same exit.
 */
@Composable
fun Modal(
    onDismiss: () -> Unit,
    cardModifier: Modifier = Modifier,
    content: @Composable ModalScope.() -> Unit,
) {
    val reducedMotion = rememberReducedMotion()
    var pendingAfterExit by remember { mutableStateOf<(() -> Unit)?>(null) }
    val closing = pendingAfterExit != null
    val scope = remember {
        ModalScope { then -> if (pendingAfterExit == null) pendingAfterExit = then }
    }

    LaunchedEffect(closing) {
        val then = pendingAfterExit ?: return@LaunchedEffect
        delay(Durations.EXIT.toLong())
        then()
    }

    DisposableEffect(Unit) {
        val listener = EventListener { e ->
            if (e.asDynamic().key == "Escape") scope.dismiss(onDismiss)
        }
        document.addEventListener("keydown", listener)
        onDispose { document.removeEventListener("keydown", listener) }
    }

    val scrimAnimation = if (closing) {
        FadeOutKeyframes.toAnimation(Durations.EXIT.ms, Ease.Out.fn, fillMode = AnimationFillMode.Forwards)
    } else {
        FadeInKeyframes.toAnimation(Durations.MODAL.ms, Ease.Out.fn, fillMode = AnimationFillMode.Backwards)
    }
    val cardAnimation = when {
        closing && reducedMotion -> scrimAnimation
        closing -> ModalCardOutKeyframes.toAnimation(Durations.EXIT.ms, Ease.Out.fn, fillMode = AnimationFillMode.Forwards)
        reducedMotion -> scrimAnimation
        else -> ModalCardInKeyframes.toAnimation(Durations.MODAL.ms, Ease.Out.fn, fillMode = AnimationFillMode.Backwards)
    }

    Div(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .styleModifier { property("background-color", "rgba(0, 0, 0, 0.6)") }
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .styleModifier { property("z-index", "1000") }
            .animation(scrimAnimation)
            .pointerEvents(if (closing) PointerEvents.None else PointerEvents.Auto)
            .onClick { scope.dismiss(onDismiss) }
            .toAttrs()
    ) {
        Div(
            Modifier
                .backgroundColor(SitePalette.surface)
                .border(1.px, LineStyle.Solid, SitePalette.border)
                .borderRadius(12.px)
                .padding(1.5.cssRem)
                .styleModifier {
                    property("min-width", "20rem")
                    property("max-width", "28rem")
                    property("width", "90vw")
                }
                .then(cardModifier)
                .animation(cardAnimation)
                .onClick { it.stopPropagation() }
                .toAttrs()
        ) {
            scope.content()
        }
    }
}
